import React, { useEffect, useMemo, useRef, useState } from 'react';
import OpenedIcon from '@mui/icons-material/KeyboardArrowDown';
import ClosedIcon from '@mui/icons-material/KeyboardArrowUp';
import { useHistory, useLocation } from 'react-router-dom';
import { Tooltip } from '@mui/material';
import ReactPlayer from 'react-player';
import cx from 'classnames';

import { updateNotes, updateQualifiers } from '../../../store/videos/videoSlice';
import { emptyComposerStackAction, triggerRefetch } from '../../../store/sharedStore/sharedStoreSlice';
import { ReactComponent as CreateComposerIcon } from '../../../assets/icons/composer.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import { buildQueryString, parseQueryString } from '../../../helpers';
import { findSearchValue } from '../../../helpers/getSearchValue';
import { STATUS, VIDEOPAGE_TYPE } from '../../../enums/ENUMS';
import styles from './VideoComposerList.module.scss';
import {
  triggerRefresh,
  updateCustomTime,
  getAllComposersThunk,
  updateComposerPlayControls,
  getAllSharedComposerThunk,
  updateProgressPosition,
  updateComposerPixelPosition,
  getAllContentMyComposers,
  clearComposerData,
} from '../../../store/composers/composersSlice';
import { getVideofeedInfo } from '../../../api';
import LinearProgress from '../LinearProgress';
import FullPageLoader from '../FullPageLoader';
import { PATHS } from '../../../enums/PATHS';

const VideoMyComposerList = () => {
  const dispatch = useAppDispatch();
  const { composers, status, composerPlayControl, composerVideoClips, currentComposerVideos, anglesCount } = useAppSelector(
    (state) => state.composersStore,
  );
  const { selectedVideoQuality, showSidebar, currentVideoThatInFrame } = useAppSelector((state) => state.sharedStore);
  const { currentUser, myOrganization } = useAppSelector((state) => state.user);
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search);
  const feedId = findSearchValue('feedId');
  const allContent = findSearchValue('allContent');

  const [selectedFeed, setSelectedFeed] = useState('');
  const activeVideos = composerVideoClips?.length ? composerVideoClips : currentComposerVideos;
  const playingAngleIndex = activeVideos.findIndex((video) => video && String(video.id) === String(currentVideoThatInFrame?.id));
  const selectedClip = playingAngleIndex >= 0 ? playingAngleIndex : Number(findSearchValue('clip') || 0);

  const fetchData = async () => {
    try {
      const res = await getVideofeedInfo(feedId!); // CUT THIS
      if (allContent) {
        console.log('test');
        dispatch(getAllContentMyComposers(+myOrganization?.id));
      } else if (res?.data && currentUser?.id === res.data?.created_by?.id) {
        dispatch(getAllComposersThunk());
      } else {
        dispatch(getAllSharedComposerThunk());
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (feedId && myOrganization?.id) {
      setSelectedFeed(feedId as string);
      fetchData();
      setTimeout(() => {
        handleScrollToFeed(feedId);
      }, 1500);
    }
  }, [feedId, myOrganization?.id]);

  const onPanelChange = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.currentTarget.id !== selectedFeed) {
      dispatch(clearComposerData());
      dispatch(updateNotes([]));
      dispatch(updateQualifiers([]));
      dispatch(updateProgressPosition(0));
      dispatch(updateComposerPlayControls({ ...composerPlayControl.composerControls, playedSeconds: 0 }));
      dispatch(updateComposerPixelPosition(0));
      const paramsObj = parseQueryString(window.location.search);
      const comp = composers.findIndex((c) => c.id === e.currentTarget.id);
      if (findSearchValue('sharedComposer')) {
        paramsObj.feedId = composers[comp].id;
        paramsObj.feed = `${comp}`;
        paramsObj.clipId = composers[comp].videos[0].id;
        paramsObj.clip = '0';
        paramsObj.pageType = VIDEOPAGE_TYPE.MY_COMPOSER;
        paramsObj.sharedComposer = 'true';

        history.push(`${PATHS.COMPOSER_PAGE.path}?${buildQueryString(paramsObj)}`);
      } else {
        paramsObj.feedId = composers[comp].id;
        paramsObj.feed = `${comp}`;
        paramsObj.clipId = composers[comp].videos[0].id;
        paramsObj.clip = '0';
        paramsObj.pageType = VIDEOPAGE_TYPE.MY_COMPOSER;
        if (paramsObj?.sharedComposer) {
          delete paramsObj.sharedComposer;
        }
        history.push(`${PATHS.COMPOSER_PAGE.path}?${buildQueryString(paramsObj)}`);
      }
      location.search = searchParams.toString();
      setSelectedFeed(e.currentTarget.id);
      dispatch(triggerRefresh());
      dispatch(triggerRefetch());
      dispatch(updateCustomTime(0));
      dispatch(emptyComposerStackAction());
    }
  };

  const onClipChange = (e: React.MouseEvent<HTMLDivElement>) => {
    if (Number(e.currentTarget.id) !== selectedClip) {
      const nextVideo = activeVideos[Number(e.currentTarget.id)];
      if (!nextVideo) return;
      const paramsObj = parseQueryString(window.location.search);
      let compIndex = composers.findIndex((c) => c.id === selectedFeed);
      if (findSearchValue('sharedComposer')) {
        paramsObj.clipId = nextVideo.id;
        paramsObj.feed = `${compIndex}`;
        paramsObj.clip = e.currentTarget.id;
        paramsObj.pageType = VIDEOPAGE_TYPE.MY_COMPOSER;
        paramsObj.sharedComposer = 'true';

        window.history.replaceState(null, '', `?${buildQueryString(paramsObj)}`);
      } else {
        paramsObj.clipId = nextVideo.id;
        paramsObj.feed = `${compIndex}`;
        paramsObj.clip = e.currentTarget.id;
        paramsObj.pageType = VIDEOPAGE_TYPE.MY_COMPOSER;
        paramsObj.sharedComposer = 'true';
        if (paramsObj?.sharedComposer) {
          delete paramsObj.sharedComposer;
        }

        window.history.replaceState(null, '', `?${buildQueryString(paramsObj)}`);
      }
      dispatch(triggerRefresh());
      const isPlayedHack = !composerPlayControl.composerControls.playing;
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: isPlayedHack,
        }),
      );
      dispatch(
        updateComposerPlayControls({
          ...composerPlayControl.composerControls,
          playing: !isPlayedHack,
        }),
      );
    }
  };

  const videoQuality = useMemo(() => {
    if (selectedVideoQuality) {
      return selectedVideoQuality.url;
    }
  }, [selectedVideoQuality]);

  const feedRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleScrollToFeed = (feedId: string) => {
    if (feedRefs.current[feedId]) {
      const element = feedRefs.current[feedId];
      if (element) {
        const rect = element.getBoundingClientRect();
        const inView =
          rect.top >= 0 &&
          rect.left >= 0 &&
          rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
          rect.right <= (window.innerWidth || document.documentElement.clientWidth);

        if (!inView) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }
  };

  return (
    <div className={cx(styles.fileListContainer, !showSidebar && styles.compact)}>
      <div className={styles.wrapper}>
        {status === STATUS.LOADING && <LinearProgress />}
        {status === STATUS.LOADING && <FullPageLoader loading />}
        {composers.length > 0 && composerPlayControl.currentComposer && (
          <>
            {showSidebar && <div className={styles.title}>{composerPlayControl?.currentComposer?.title}</div>}
            <div className={cx(styles.fileList, !showSidebar && styles.fileListCompact, 'CustomScrollbar')}>
              {composers.map((feed, i) => (
                <div
                  ref={(el) => (feedRefs.current[feed.id] = el)}
                  key={feed.id}
                  className={cx(styles.accordion, selectedFeed === feed.id && styles.activeColor)}
                >
                  {feed?.videos[0] && (
                    <div onClick={onPanelChange} className={cx(styles.fileItem, !showSidebar && styles.fileItemCompact)} id={`${feed.id}`}>
                      <Tooltip title={feed.title} arrow placement="right">
                        <div className={cx(styles.leftSide, !showSidebar && styles.leftSideCompact)}>
                          {feed.thumbnail_url ? (
                            <img src={feed.thumbnail_url} alt="" className={styles.fileThumbnail} />
                          ) : (
                            <CreateComposerIcon className={styles.fileIcon} />
                          )}
                          {showSidebar && <div className={styles.fileTitle}>{feed.title}</div>}
                        </div>
                      </Tooltip>
                      <div className={styles.rightSide} style={{ marginRight: '5px' }}>
                        {selectedFeed === feed.id ? <OpenedIcon className={styles.activeColor} /> : <ClosedIcon />}
                      </div>
                    </div>
                  )}

                  {!feed?.videos[0] && (
                    <div onClick={onPanelChange} className={cx(styles.fileItem, !showSidebar && styles.fileItemCompact)} id={`${feed.id}`}>
                      <Tooltip arrow title={`🚫 ${feed.title}`} placement="right">
                        <div className={cx(styles.leftSide, !showSidebar && styles.leftSideCompact)}>
                          {feed.thumbnail_url ? (
                            <img src={feed.thumbnail_url} alt="" className={styles.fileThumbnail} />
                          ) : (
                            <CreateComposerIcon className={styles.fileIcon} />
                          )}
                          {showSidebar && <div className={styles.fileTitle}>{feed.title}</div>}
                        </div>
                      </Tooltip>
                      <div className={styles.rightSide} style={{ marginRight: '5px' }}>
                        {selectedFeed === feed.id ? <OpenedIcon className={styles.activeColor} /> : <ClosedIcon />}
                      </div>
                    </div>
                  )}

                  {selectedFeed === feed.id && (
                    <div className={cx(styles.cliplist, !showSidebar && styles.cliplistCompact)}>
                      {composerVideoClips?.length
                        ? Array.from({ length: anglesCount }).map((_, index) => {
                            if (composerVideoClips[index]) {
                              return (
                                <Tooltip title={composerVideoClips[index]?.name} placement="right-end">
                                  <div
                                    className={cx(
                                      selectedClip === index ? styles.activeClip : styles.clip,
                                      !showSidebar && styles.clipCompact,
                                    )}
                                    key={composerVideoClips[index].id}
                                    onClick={onClipChange}
                                    id={`${index}`}
                                  >
                                    {showSidebar ? `Angle ${index + 1}` : index + 1}
                                    {/* <ReactPlayer
                                    responsetype="stream"
                                    url={videoQuality}
                                    preload="true"
                                    style={{ display: 'none' }}
                                    volume={0}
                                    playing
                                  /> */}
                                  </div>
                                </Tooltip>
                              );
                            } else {
                              return (
                                <div
                                  className={cx(styles.clip, styles.disabledClip, !showSidebar && styles.clipCompact)}
                                  key={`${index}_dummy`}
                                  id={`${index}`}
                                >
                                  {showSidebar ? `Angle ${index + 1}` : index + 1}
                                </div>
                              );
                            }
                          })
                        : Array.from({ length: anglesCount }).map((_, index) => {
                            if (currentComposerVideos[index]) {
                              return (
                                <Tooltip title={currentComposerVideos[index]?.name} placement="right-end">
                                  <div
                                    className={cx(
                                      selectedClip === index ? styles.activeClip : styles.clip,
                                      !showSidebar && styles.clipCompact,
                                    )}
                                    key={currentComposerVideos[index].id}
                                    onClick={onClipChange}
                                    id={`${index}`}
                                  >
                                    {showSidebar ? `Angle ${index + 1}` : index + 1}
                                    <ReactPlayer
                                      responsetype="stream"
                                      url={videoQuality}
                                      preload="true"
                                      style={{ display: 'none' }}
                                      volume={0}
                                      playing
                                    />
                                  </div>
                                </Tooltip>
                              );
                            } else {
                              return (
                                <div
                                  className={cx(styles.clip, styles.disabledClip, !showSidebar && styles.clipCompact)}
                                  key={`${index}_dummy`}
                                  id={`${index}`}
                                >
                                  {showSidebar ? `Angle ${index + 1}` : index + 1}
                                </div>
                              );
                            }
                          })}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default VideoMyComposerList;
